import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
  brokerAlertVariant,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TradeLogGradientBadge } from "@/components/trade-log-gradient-badge";
import { cn } from "@/lib/utils";
import type { CustomCsvWizardState } from "../../../types/customCsvImport";
import {
  describeCsvPreviewParseIssue,
  readCsvSampleRowsFromFile,
} from "../../../utils/customCsvImport";
import {
  csvImportWarningAlertStatus,
  formatCsvImportWarningDisplayLine,
  highestWarningSeverity,
  sortCsvImportWarnings,
  summarizeCsvImportWarningsForDisplay,
} from "../../../utils/customCsvImportWarnings";
import {
  enrichPreviewErrors,
  summarizePreviewErrors,
} from "../../../utils/customCsvPreviewDiagnostics";
import { customCsvPreviewLabels } from "../../../utils/customCsvPreviewLabels";
import {
  CUSTOM_CSV_FIELD_CARD_CLASS,
  CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS,
} from "./customCsvWizardUi";
import PreviewErrorsPanel from "./PreviewErrorsPanel";
import SampleRowsTable, { PREVIEW_SAMPLE_MAX_ROWS, SampleOrdersTable } from "./SampleRowsTable";

export interface CustomCsvPreviewStepProps {
  state: CustomCsvWizardState;
  fileHeaders: string[];
  isLoading: boolean;
  error: string | null;
  onRunPreview: () => void;
}

const STATS_HEAD_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-normal normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

const STATS_CELL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-700";

function PreviewSectionCard({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Card
      data-custom-csv-preview-card=""
      className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full min-w-0")}
    >
      <CardContent className={CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS}>{children}</CardContent>
    </Card>
  );
}

const CustomCsvPreviewStep = ({
  state,
  fileHeaders,
  isLoading,
  error,
  onRunPreview,
}: CustomCsvPreviewStepProps): React.ReactElement => {
  const preview = state.preview;
  const parseIssue = preview ? describeCsvPreviewParseIssue(preview, fileHeaders) : null;
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);

  useEffect(() => {
    if (!state.csvFile) {
      setCsvRows([]);
      return;
    }
    let cancelled = false;
    readCsvSampleRowsFromFile(state.csvFile, 10_000).then((rows) => {
      if (!cancelled) setCsvRows(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [state.csvFile, preview?.preview_token]);

  const enrichedErrors = useMemo(() => {
    if (!preview?.errors.length) return [];
    return enrichPreviewErrors(preview.errors, {
      csvRows,
      columnMapping: state.columnMapping,
      valueTransforms: state.valueTransforms,
    });
  }, [preview?.errors, csvRows, state.columnMapping, state.valueTransforms]);

  const errorSummaries = useMemo(
    () => summarizePreviewErrors(enrichedErrors),
    [enrichedErrors]
  );

  const sortedWarnings = useMemo(
    () => sortCsvImportWarnings(preview?.warnings ?? []),
    [preview?.warnings]
  );

  const warningDisplayLines = useMemo(
    () => summarizeCsvImportWarningsForDisplay(sortedWarnings),
    [sortedWarnings]
  );

  const previewLabels = useMemo(
    () =>
      customCsvPreviewLabels(
        preview?.import_mode ?? state.importMode,
        preview?.layout_shape_id ?? state.layoutShapeId
      ),
    [preview?.import_mode, preview?.layout_shape_id, state.importMode, state.layoutShapeId]
  );

  const pairedPreviewRowCount = preview
    ? Math.min(preview.sample_rows.length, PREVIEW_SAMPLE_MAX_ROWS)
    : 0;

  const warningTone = highestWarningSeverity(sortedWarnings);
  const warningVariant = brokerAlertVariant(csvImportWarningAlertStatus(warningTone));

  return (
    <div className="flex flex-col gap-5" data-custom-csv-preview-step="">
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="h-8 w-fit shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
        onClick={onRunPreview}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Running preview…
          </>
        ) : preview ? (
          "Re-run preview"
        ) : (
          "Run preview"
        )}
      </Button>

      {error ? (
        <Alert variant="error" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="error" />
          <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>Preview failed</AlertTitle>
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>{error}</AlertDescription>
        </Alert>
      ) : null}

      {preview ? (
        <>
          {parseIssue ? (
            <Alert variant="warning" className={BROKER_ALERT_CLASS}>
              <BrokerAlertIcon tone="warning" />
              <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>
                CSV parsed differently on the server
              </AlertTitle>
              <AlertDescription className={BROKER_ALERT_DESC_CLASS}>{parseIssue}</AlertDescription>
            </Alert>
          ) : preview.can_import ? (
            <Alert variant="success" className={BROKER_ALERT_CLASS}>
              <BrokerAlertIcon tone="success" />
              <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>Ready to import</AlertTitle>
              <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
                {previewLabels.readyReviewLine}
              </AlertDescription>
            </Alert>
          ) : null}

          <PreviewSectionCard>
            <Field className="gap-2">
              <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Preview summary</FieldLabel>
              <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                {preview.can_import
                  ? "Review counts before continuing to import."
                  : "Fix errors or warnings before importing."}
              </FieldDescription>
              <div
                data-shadcn-data-table
                data-custom-csv-preview-stats-table=""
                className="w-full overflow-x-auto overflow-y-hidden rounded-[6px] border border-border bg-background"
              >
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className={STATS_HEAD_CLASS}>Rows read</TableHead>
                      <TableHead className={STATS_HEAD_CLASS}>
                        {previewLabels.builtStatLabel}
                      </TableHead>
                      <TableHead className={STATS_HEAD_CLASS}>Errors</TableHead>
                      <TableHead className={STATS_HEAD_CLASS}>Warnings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className={STATS_CELL_CLASS}>{preview.stats.rows_read}</TableCell>
                      <TableCell className={STATS_CELL_CLASS}>
                        {preview.stats.orders_built}
                      </TableCell>
                      <TableCell className={STATS_CELL_CLASS}>{preview.stats.errors}</TableCell>
                      <TableCell className={STATS_CELL_CLASS}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{preview.stats.warnings}</span>
                          <TradeLogGradientBadge
                            tone={preview.stats.warnings > 0 ? "negative" : "positive"}
                          >
                            {preview.stats.warnings > 0 ? "Has warnings" : "Ready to import"}
                          </TradeLogGradientBadge>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Field>
          </PreviewSectionCard>

          {preview.errors.length > 0 ? (
            <PreviewErrorsPanel enrichedErrors={enrichedErrors} summaries={errorSummaries} />
          ) : null}

          {sortedWarnings.length > 0 ? (
            <Field className="gap-2">
              <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Warnings</FieldLabel>
              <Alert
                variant={warningVariant}
                className={cn(BROKER_ALERT_CLASS, "has-[>svg]:gap-x-3 sm:has-[>svg]:gap-x-4")}
              >
                <BrokerAlertIcon
                  tone={
                    warningVariant === "error"
                      ? "error"
                      : warningVariant === "warning"
                        ? "warning"
                        : "info"
                  }
                />
                <AlertDescription className={cn(BROKER_ALERT_DESC_CLASS, "col-start-2 min-w-0")}>
                  <div className="flex flex-col gap-2.5">
                    {warningDisplayLines.map((line) => (
                      <p key={line.codes.join("-") + line.message} className="m-0 leading-5">
                        {formatCsvImportWarningDisplayLine(line)}
                      </p>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            </Field>
          ) : null}

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <PreviewSectionCard>
              <Field className="gap-2">
                <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Sample from your file</FieldLabel>
                <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                  First rows from your CSV. Highlighted columns are mapped to Tessera fields.
                </FieldDescription>
                <SampleRowsTable
                  headers={preview.headers}
                  rows={preview.sample_rows}
                  columnMapping={state.columnMapping}
                  maxRows={PREVIEW_SAMPLE_MAX_ROWS}
                />
              </Field>
            </PreviewSectionCard>

            <PreviewSectionCard>
              <Field className="gap-2">
                <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
                  {previewLabels.previewSectionTitle}
                </FieldLabel>
                <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                  How the server interpreted your mapping. Check times, side, size, and P/L look
                  right before importing.
                </FieldDescription>
                <SampleOrdersTable
                  orders={preview.sample_orders}
                  importMode={preview.import_mode}
                  layoutShapeId={preview.layout_shape_id ?? state.layoutShapeId}
                  timeZone={state.timeZone}
                  totalBuilt={preview.stats.orders_built}
                  noSampleBuiltLabel={previewLabels.noSampleBuilt}
                  maxRows={pairedPreviewRowCount}
                />
              </Field>
            </PreviewSectionCard>
          </div>
        </>
      ) : (
        <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>{previewLabels.runPreviewHint}</p>
      )}
    </div>
  );
};

export default CustomCsvPreviewStep;
